'use client';

import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, CheckCircle2, AlertTriangle, Users, Settings, MessageSquare } from 'lucide-react';
import Image from 'next/image';

const monitoredEvents = [
  { icon: Users, label: 'Member Joins & Leaves', description: 'Track all member activity' },
  { icon: AlertTriangle, label: 'Bans & Kicks', description: 'Monitor moderation actions' },
  { icon: Shield, label: 'Role Changes', description: 'Log permission updates' },
  { icon: Settings, label: 'Channel Modifications', description: 'Track server structure changes' },
  { icon: MessageSquare, label: 'Voice Moves', description: 'Monitor voice channel activity' },
];

const protectionFeatures = [
  {
    title: 'Real-Time Logging',
    description: 'All important events are logged instantly to your dedicated antinuke channel',
    icon: CheckCircle2,
  },
  {
    title: 'Whitelist System',
    description: 'Add trusted admins to a whitelist so their actions are ignored by the system',
    icon: Shield,
  },
  {
    title: 'Audit Log Integration',
    description: 'Attempts to identify who performed each action by checking Discord audit logs',
    icon: Settings,
  },
  {
    title: 'Non-Intrusive',
    description: 'Records and alerts only—does not automatically ban users. You stay in control.',
    icon: AlertTriangle,
  },
];

export function AntinukeShowcase() {
  return (
    <section className="py-24 bg-gradient-to-b from-[#0f0f0f] to-[#1a1a1a]">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-6">
            <Shield className="h-5 w-5 text-red-500" />
            <span className="text-sm font-semibold text-red-500">SECURITY FEATURE</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Advanced Antinuke Protection
          </h2>
          <p className="text-xl text-[#b9bbbe] max-w-3xl mx-auto">
            Keep your server safe with comprehensive event logging and monitoring. Get instant alerts when important changes occur.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Main content grid */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Left: Discord embed mockup */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center"
            >
              <Card className="bg-[#1a1a1a] border-red-500/20 shadow-2xl shadow-red-500/10 overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-red-500/10 to-red-600/10 border-b border-red-500/20">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-white text-lg">Yokki</CardTitle>
                        <Badge className="bg-[#5865F2] text-white text-[10px] px-1.5 py-0.5">BOT</Badge>
                      </div>
                      <p className="text-xs text-[#b9bbbe]">Today at 2:47 PM</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="p-6">
                    <Image
                      src="/images/generated/discord-embed-mockup.png"
                      alt="Antinuke Status"
                      width={500}
                      height={300}
                      className="w-full rounded-lg"
                    />
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#b9bbbe]">Antinuke Status:</span>
                        <Badge className="bg-[#3ba55d] text-white">ACTIVE</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#b9bbbe]">Protected Events:</span>
                        <span className="text-white font-semibold">47</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#b9bbbe]">Last Threat Blocked:</span>
                        <span className="text-white font-semibold">2 minutes ago</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right: Monitored events */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">What We Monitor</h3>
              <div className="space-y-4">
                {monitoredEvents.map((event, index) => {
                  const Icon = event.icon;
                  return (
                    <motion.div
                      key={event.label}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card className="bg-[#252525] border-[#b9bbbe]/10 hover:border-red-500/30 transition-all">
                        <CardHeader className="pb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                              <Icon className="h-5 w-5 text-red-500" />
                            </div>
                            <div>
                              <CardTitle className="text-white text-base">{event.label}</CardTitle>
                              <CardDescription className="text-sm">{event.description}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Protection features grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-white mb-8 text-center">How It Works</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {protectionFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="h-full bg-[#1a1a1a] border-red-500/20 hover:bg-[#252525] transition-all text-center">
                      <CardHeader>
                        <div className="h-14 w-14 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-3">
                          <Icon className="h-7 w-7 text-red-500" />
                        </div>
                        <CardTitle className="text-white text-lg">{feature.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-[#b9bbbe]">
                          {feature.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Quick setup CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 text-center"
          >
            <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20 p-8">
              <h3 className="text-2xl font-bold text-white mb-3">
                Setup in One Command
              </h3>
              <p className="text-[#b9bbbe] mb-6">
                Get started with antinuke protection instantly
              </p>
              <code className="inline-block px-6 py-3 bg-[#252525] text-[#5865F2] rounded-lg font-mono text-lg border border-[#5865F2]/30">
                ,antinuke setup
              </code>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
